import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/apiClient";
import { apiData } from "../../utils/apiResult";

const publicBase = () =>
  typeof window !== "undefined" ? window.location.origin : "https://trust.mito.money";

export const createReceiveMoneyRequest = createAsyncThunk(
  "ReceiveMoney/Create",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/Create", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

/** Amount & Currencies Continue — Direction R KYC pre-clear before a request exists. */
export const checkReceiveMoneyReceiverKyc = createAsyncThunk(
  "ReceiveMoney/CheckReceiverKyc",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/CheckReceiverKyc", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const updateReceiveMoneyRequest = createAsyncThunk(
  "ReceiveMoney/Update",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/Update", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const resendReceiveMoneyEmail = createAsyncThunk(
  "ReceiveMoney/ResendEmail",
  async (data: { id: string; payerEmail?: string }) => {
    const response = await api.post("ReceiveMoney/ResendEmail", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const listReceiveMoneyRequests = createAsyncThunk(
  "ReceiveMoney/List",
  async () => {
    const response = await api.get(
      `ReceiveMoney/List?publicBaseUrl=${encodeURIComponent(publicBase())}`
    );
    return response;
  }
);

export const getReceiveMoneyByToken = createAsyncThunk(
  "ReceiveMoney/ByToken",
  async (token: string) => {
    const response = await api.get(
      `ReceiveMoney/ByToken/${encodeURIComponent(token)}?publicBaseUrl=${encodeURIComponent(publicBase())}`
    );
    return response;
  }
);

export const getPrimarySettlementBank = createAsyncThunk(
  "ReceiveMoney/GetPrimarySettlementBank",
  async () => {
    const response = await api.get("ReceiveMoney/PrimarySettlementBank");
    return response;
  }
);

export const setPrimarySettlementBank = createAsyncThunk(
  "ReceiveMoney/SetPrimarySettlementBank",
  async (beneficiaryId: string, { rejectWithValue }) => {
    try {
      const response = await api.post("ReceiveMoney/PrimarySettlementBank", {
        data: { beneficiaryId },
      });
      return response;
    } catch (error) {
      return rejectWithValue(
        typeof error === "string"
          ? error
          : "Could not set your primary account."
      );
    }
  }
);

export const requestReceiveMoneyEmailCode = createAsyncThunk(
  "ReceiveMoney/RequestEmailCode",
  async (data: {
    token: string;
    email: string;
    firstName?: string;
    lastName?: string;
    gender?: string;
    isBusiness?: boolean;
    businessName?: string;
  }) => {
    const response = await api.post("ReceiveMoney/RequestEmailCode", { data });
    return response;
  }
);

export const confirmReceiveMoneyAsSignedInUser = createAsyncThunk(
  "ReceiveMoney/PayAsSignedInUser",
  async (token: string) => {
    const response = await api.post("ReceiveMoney/PayAsSignedInUser", {
      data: { token, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const confirmReceiveMoneyEmail = createAsyncThunk(
  "ReceiveMoney/ConfirmEmail",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/ConfirmEmail", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const submitReceiveMoneyPayerDetails = createAsyncThunk(
  "ReceiveMoney/PayerDetails",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/PayerDetails", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const setReceiveMoneyPayerPassword = createAsyncThunk(
  "ReceiveMoney/SetPassword",
  async (data: { token: string; password: string }) => {
    const response = await api.post("ReceiveMoney/SetPassword", { data });
    return response;
  }
);

export const submitReceiveMoneyPayment = createAsyncThunk(
  "ReceiveMoney/SubmitPayment",
  async (data: Record<string, any>) => {
    const response = await api.post("ReceiveMoney/SubmitPayment", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

export const startReceiveMoneyHostedJourney = createAsyncThunk(
  "ReceiveMoney/StartHostedJourney",
  async (data: {
    token: string;
    countryIso3: string;
    firstName?: string;
    lastName?: string;
    isBusiness?: boolean;
    businessName?: string;
  }) => {
    const response = await api.post("ReceiveMoney/StartHostedJourney", {
      data: { ...data, publicBaseUrl: publicBase() },
    });
    return response;
  }
);

const receiveMoneySlice = createSlice({
  name: "receiveMoney",
  initialState: {
    list: [] as any[],
    current: null as any,
    loading: false,
  },
  reducers: {
    clearCurrent(state) {
      state.current = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listReceiveMoneyRequests.fulfilled, (state, action) => {
        state.list = apiData<any[]>(action.payload) || [];
      })
      .addCase(getReceiveMoneyByToken.fulfilled, (state, action) => {
        state.current = apiData(action.payload);
      })
      .addCase(confirmReceiveMoneyAsSignedInUser.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(confirmReceiveMoneyEmail.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(submitReceiveMoneyPayerDetails.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(submitReceiveMoneyPayment.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) state.current = row;
      })
      .addCase(createReceiveMoneyRequest.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) {
          state.list = [row, ...state.list.filter((x) => (x.id || x.Id) !== (row.id || row.Id))];
        }
      })
      .addCase(updateReceiveMoneyRequest.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) {
          state.list = state.list.map((x) =>
            (x.id || x.Id) === (row.id || row.Id) ? { ...x, ...row } : x
          );
          if (state.current && (state.current.id || state.current.Id) === (row.id || row.Id)) {
            state.current = { ...state.current, ...row };
          }
        }
      })
      .addCase(resendReceiveMoneyEmail.fulfilled, (state, action) => {
        const row = apiData(action.payload);
        if (row) {
          state.list = state.list.map((x) =>
            (x.id || x.Id) === (row.id || row.Id) ? { ...x, ...row } : x
          );
        }
      });
  },
});

export const { clearCurrent } = receiveMoneySlice.actions;
export default receiveMoneySlice.reducer;
